/**
 * WAIncognito — hides read receipts / typing on WhatsApp Web
 *
 * Its single web-accessible-resource group globs `lib/*`, `core/*` and `images/*`, but
 * matched only to `https://web.whatsapp.com/*`. Those resources are genuinely
 * unreachable from anywhere else, so a negative result on an arbitrary page proves
 * nothing — the probe simply is not on WhatsApp Web.
 *
 * So this extension IS detectable, but only from WhatsApp Web. There is no
 * `use_dynamic_url`, so from that origin the concrete files below resolve at a fixed URL.
 *
 * Manifest verified against version 2.5.6.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'alhmbbnlcggfcjjfihglopfopcbigmil';

export const waIncognito: ExtensionDefinition = {
  id: ID,
  name: 'WAIncognito',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  requiredOrigin: 'https://web.whatsapp.com',
  notes:
    'All WAR are scoped to web.whatsapp.com, so only that site can detect it. No ' +
    'dynamic URL, so from there the core/lib/image files are fetchable at a fixed URL.',
  signals: [
    {
      id: 'war-core-interception-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch core/interception.js — the message-interception script, exposed only to ' +
        'web.whatsapp.com and served only when the extension is installed.',
      weight: 1,
      run: () => probeFetch(ID, 'core/interception.js'),
    },
    {
      id: 'war-moduleraid-js',
      kind: 'web-accessible-resource',
      description: 'Fetch lib/moduleraid.js — a bundled library, an independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'lib/moduleraid.js'),
    },
    {
      id: 'war-incognito-png',
      kind: 'web-accessible-resource',
      description: 'Fetch images/incognito.png — a bundled icon, a third scoped path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'images/incognito.png'),
    },
  ],
};
